import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Typography,
  Drawer,
  List,
  ListItemButton,
  Badge,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState, memo, type ReactElement, type MouseEvent } from 'react';
import { addSentryBreadcrumb, captureException, captureMessage } from '../../config/sentry';
import cwyLogo from '../../assets/cw-red-logo.svg';
import { useAuth } from '../../shared/contexts/AuthContext';
import { useNotifications } from '../../shared/contexts/NotificationContext';
import type { Notification } from '../../shared/services/notificationService';
import { useSocket } from '../../contexts/SocketContext';
import { getSidebarUnreadCounts, type SidebarUnreadCounts } from '../../shared/services/sidebarService';
import { withLedgerBookQuery } from '../../features/ledger/utils/ledgerBookSession';
import { navigateToAgentPhoneScreen } from '../../services/agentPhone/agentPhoneBridge';
import { useAgentPhoneCallState } from '../../hooks/useAgentPhoneCallState';
import { Z_INDEX } from '../../constants/zIndex';
import { getUnreadCount } from '../../features/mailbox/api';
import { getCachedSummary, getCachedUnread } from '../../features/mailbox/cache';
import {
  TOP_BAR_HEIGHT,
  SHOW_NAV_UNREAD_COUNTS,
  buildAppNav,
  isNavItemActive,
  type NavIconKey,
  type NavItem,
} from './navConfig';

const NAV_WATCHDOG_MS = 4000;
const NAV_TEXT_PRIMARY = '#0f0f0f';
const NAV_TEXT_SECONDARY = '#606060';
const ICON_SX = { fontSize: 22 };

const ICON_MAP: Record<NavIconKey, ReactElement> = {
  dashboard: <HomeOutlinedIcon sx={ICON_SX} />,
  assignment: <AssignmentOutlinedIcon sx={ICON_SX} />,
  whatsapp: <ChatOutlinedIcon sx={ICON_SX} />,
  accountBalance: <AccountBalanceOutlinedIcon sx={ICON_SX} />,
  calendar: <EventAvailableOutlinedIcon sx={ICON_SX} />,
  meetingRoom: <MeetingRoomOutlinedIcon sx={ICON_SX} />,
  headset: <HeadsetMicOutlinedIcon sx={ICON_SX} />,
  trendingDown: <TrendingDownOutlinedIcon sx={ICON_SX} />,
  description: <FolderOutlinedIcon sx={ICON_SX} />,
  article: <ArticleOutlinedIcon sx={ICON_SX} />,
  forum: <ForumOutlinedIcon sx={ICON_SX} />,
  person: <PersonOutlinedIcon sx={ICON_SX} />,
  personOutline: <PersonOutlineIcon sx={ICON_SX} />,
  settings: <SettingsOutlinedIcon sx={ICON_SX} />,
  tableChart: <TableChartOutlinedIcon sx={ICON_SX} />,
  smartToy: <SmartToyOutlinedIcon sx={ICON_SX} />,
  currencyExchange: <CurrencyExchangeOutlinedIcon sx={ICON_SX} />,
  accessTime: <AccessTimeOutlinedIcon sx={ICON_SX} />,
  trendingUp: <TrendingUpOutlinedIcon sx={ICON_SX} />,
  school: <SchoolOutlinedIcon sx={ICON_SX} />,
  policy: <PolicyOutlinedIcon sx={ICON_SX} />,
  gavel: <GavelOutlinedIcon sx={ICON_SX} />,
  taskAlt: <TaskAltOutlinedIcon sx={ICON_SX} />,
  notifications: <NotificationsNoneOutlinedIcon sx={ICON_SX} />,
  mail: <MailOutlineIcon sx={ICON_SX} />,
  cameraAlt: <CameraAltOutlinedIcon sx={ICON_SX} />,
  logout: <LogoutIcon sx={ICON_SX} />,
};

function NavIcon({ iconKey }: { iconKey: NavIconKey }) {
  return ICON_MAP[iconKey] ?? <HomeOutlinedIcon sx={ICON_SX} />;
}

function UnreadBadge({ count, children }: { count?: number; children: React.ReactNode }) {
  if (!count) return <>{children}</>;
  return (
    <Badge
      badgeContent={count}
      color="error"
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: '#4caf50',
          color: 'white',
          fontSize: '0.7rem',
          fontWeight: 500,
          minWidth: 18,
          height: 18,
        },
      }}
    >
      {children}
    </Badge>
  );
}

export const TopBar = memo(function TopBar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadCount: noticeUnread, notifications: noticeItems, addNotification } = useNotifications();
  const { socket, isConnected } = useSocket();
  const callState = useAgentPhoneCallState();
  const onCall = callState.status !== 'idle';
  const [unreadCounts, setUnreadCounts] = useState<SidebarUnreadCounts>({
    notifications: 0,
    clientChats: 0,
  });

  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);
  const [adminAnchor, setAdminAnchor] = useState<null | HTMLElement>(null);
  const [userAnchor, setUserAnchor] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const pendingNavRef = useRef<{
    intended: string;
    from: string;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const counts = await getSidebarUnreadCounts();
      setUnreadCounts((prev) => {
        if (
          prev.notifications !== counts.notifications ||
          prev.clientChats !== counts.clientChats
        ) {
          return counts;
        }
        return prev;
      });
    } catch (error) {
      console.error('TopBar - Error fetching unread counts:', error);
    }
  }, []);

  useEffect(() => {
    if (!SHOW_NAV_UNREAD_COUNTS || !user?._id) return;
    fetchUnreadCounts();
  }, [user?._id, fetchUnreadCounts]);

  useEffect(() => {
    if (!SHOW_NAV_UNREAD_COUNTS || !user?._id) return;
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [user?._id, fetchUnreadCounts]);

  useEffect(() => {
    if (!SHOW_NAV_UNREAD_COUNTS || !socket || !isConnected || !user?._id) return;

    const handleClientMessage = (data: { direction?: string }) => {
      if (data?.direction === 'incoming' || data?.direction === 'inbound') {
        setUnreadCounts((prev) => ({ ...prev, clientChats: prev.clientChats + 1 }));
      }
    };
    const handleClientMessagesRead = () => {
      setUnreadCounts((prev) => {
        if (prev.clientChats > 0) fetchUnreadCounts();
        return prev;
      });
    };
    const handleNewNotification = () => {
      setUnreadCounts((prev) => ({ ...prev, notifications: prev.notifications + 1 }));
    };
    const handleNotificationRead = () => {
      setUnreadCounts((prev) => ({
        ...prev,
        notifications: Math.max(0, prev.notifications - 1),
      }));
    };
    const handleSidebarUnreadUpdate = () => fetchUnreadCounts();

    socket.on('client:newMessage', handleClientMessage);
    socket.on('client:messagesRead', handleClientMessagesRead);
    socket.on('notification:new', handleNewNotification);
    socket.on('notification:read', handleNotificationRead);
    socket.on('sidebar:unreadUpdate', handleSidebarUnreadUpdate);

    return () => {
      socket.off('client:newMessage', handleClientMessage);
      socket.off('client:messagesRead', handleClientMessagesRead);
      socket.off('notification:new', handleNewNotification);
      socket.off('notification:read', handleNotificationRead);
      socket.off('sidebar:unreadUpdate', handleSidebarUnreadUpdate);
    };
  }, [socket, isConnected, user?._id, fetchUnreadCounts]);

  const nav = useMemo(() => buildAppNav(user, unreadCounts), [user, unreadCounts]);
  const [mailUnread, setMailUnread] = useState(() => getCachedUnread());

  useEffect(() => {
    if (!socket || !nav.showNotifications) return;
    const onNew = (payload: {
      notificationId?: string;
      title?: string;
      message?: string;
      type?: Notification['type'];
    }) => {
      const id = payload?.notificationId;
      if (id && noticeItems.some((item) => item._id === id)) return;
      addNotification({
        _id: id || `live-${Date.now()}`,
        recipientId: String(user?._id || ''),
        type: payload?.type || 'system',
        title: payload?.title || 'Notification',
        message: payload?.message || '',
        status: 'unread',
        priority: 'medium',
        emailSent: false,
        smsSent: false,
        pushSent: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    };
    socket.on('admin:notification:new', onNew);
    return () => {
      socket.off('admin:notification:new', onNew);
    };
  }, [socket, nav.showNotifications, noticeItems, addNotification, user?._id]);

  useEffect(() => {
    if (!nav.showMailbox) return;
    const cached = getCachedSummary();
    if (!cached?.id) {
      setMailUnread(0);
      return;
    }
    let cancelled = false;
    const load = () => {
      getUnreadCount(cached.id)
        .then((count) => {
          if (!cancelled) setMailUnread(count);
        })
        .catch(() => undefined);
    };
    load();
    const timer = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [nav.showMailbox, location.pathname]);

  const closeMenus = () => {
    setMoreAnchor(null);
    setAdminAnchor(null);
    setUserAnchor(null);
    setMobileOpen(false);
  };

  const handleNavigation = useCallback(
    (path: string) => {
      const target = withLedgerBookQuery(path);
      const from = location.pathname;

      addSentryBreadcrumb('TopBar navigation clicked', 'navigation', 'info', { from, to: target });

      if (pendingNavRef.current?.timer) {
        clearTimeout(pendingNavRef.current.timer);
        pendingNavRef.current = null;
      }

      try {
        // Official RR7 option: commit this click's route update now instead of
        // startTransition, so closing a menu in the same event cannot leave the
        // URL changed while the previous page stays on screen.
        navigate(target, { flushSync: true });
      } catch (err) {
        captureException(err, {
          context: 'topbar_navigation_throw',
          intendedPath: target,
          fromPath: from,
          href: window.location.href,
        });
        closeMenus();
        return;
      }

      closeMenus();

      const intendedPath = target.split('?')[0];
      if (intendedPath === from) return;

      const timer = setTimeout(() => {
        const current = pendingNavRef.current;
        if (current && current.intended === intendedPath && window.location.pathname === from) {
          captureMessage('TopBar navigation did not complete', 'warning', {
            intendedPath,
            fromPath: from,
            currentPath: window.location.pathname,
            href: window.location.href,
            watchdogMs: NAV_WATCHDOG_MS,
          });
        }
        pendingNavRef.current = null;
      }, NAV_WATCHDOG_MS);

      pendingNavRef.current = { intended: intendedPath, from, timer };
    },
    [navigate, location.pathname]
  );

  const handleAppLinkClick = (event: MouseEvent<HTMLElement>, path: string) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
      closeMenus();
      return;
    }
    event.preventDefault();
    handleNavigation(path);
  };

  useEffect(() => {
    const pending = pendingNavRef.current;
    if (pending && location.pathname !== pending.from) {
      clearTimeout(pending.timer);
      pendingNavRef.current = null;
    }
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (pendingNavRef.current?.timer) {
        clearTimeout(pendingNavRef.current.timer);
        pendingNavRef.current = null;
      }
    };
  }, []);

  const handleLogout = () => {
    closeMenus();
    logout();
    navigate('/login');
  };

  const navButtonSx = (active: boolean) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: { xs: 52, sm: 72 },
    maxWidth: 104,
    height: TOP_BAR_HEIGHT,
    px: 0.75,
    py: 0,
    border: 'none',
    borderRadius: 0,
    background: 'none',
    boxShadow: 'none',
    cursor: 'pointer',
    textTransform: 'none' as const,
    textDecoration: 'none',
    color: active ? NAV_TEXT_PRIMARY : NAV_TEXT_SECONDARY,
    borderBottom: active ? `2px solid ${NAV_TEXT_PRIMARY}` : '2px solid transparent',
    fontWeight: active ? 600 : 400,
    transition: 'color 0.15s ease',
    '&:hover': {
      color: NAV_TEXT_PRIMARY,
      background: 'none',
      boxShadow: 'none',
    },
  });

  const renderMenuItems = (items: NavItem[]) =>
    items.map((item) => {
      const active = isNavItemActive(location.pathname, item.path);
      return (
        <MenuItem
          key={item.path}
          component={RouterLink}
          to={withLedgerBookQuery(item.path)}
          selected={active}
          onClick={(event) => handleAppLinkClick(event, item.path)}
          sx={{ minWidth: 220 }}
        >
          <ListItemIcon sx={{ color: active ? NAV_TEXT_PRIMARY : NAV_TEXT_SECONDARY }}>
            <UnreadBadge count={item.unreadCount}>
              <NavIcon iconKey={item.iconKey} />
            </UnreadBadge>
          </ListItemIcon>
          <ListItemText primary={item.text} />
        </MenuItem>
      );
    });

  const renderNavTab = ({
    key,
    label,
    active,
    onClick,
    icon,
    showChevron,
    ariaCurrent,
    to,
  }: {
    key: string;
    label: string;
    active: boolean;
    onClick: (e: MouseEvent<HTMLElement>) => void;
    icon: React.ReactNode;
    showChevron?: boolean;
    ariaCurrent?: boolean;
    to?: string;
  }) => (
    <Tooltip key={key} title={label}>
      <Box
        {...(to
          ? { component: RouterLink, to }
          : { component: 'button', type: 'button' })}
        onClick={onClick}
        aria-label={label}
        aria-current={ariaCurrent ? 'page' : undefined}
        sx={navButtonSx(active)}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            lineHeight: 0,
            width: '100%',
          }}
        >
          {icon}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.15,
            mt: 0.25,
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '0.6875rem',
              lineHeight: 1.2,
              fontWeight: active ? 600 : 400,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {label}
          </Typography>
          {showChevron && (
            <KeyboardArrowDownIcon
              sx={{ fontSize: 14, flexShrink: 0, opacity: 0.75 }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );

  const moreActive = nav.moreWork.some((item) => isNavItemActive(location.pathname, item.path));
  const adminActive = nav.adminHubs.some((item) => isNavItemActive(location.pathname, item.path));
  const notificationsActive = isNavItemActive(location.pathname, '/app/notifications');

  const mobileDrawer = (
    <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', height: '100%' }} role="presentation">
      <Box sx={{ px: 2, py: 2, borderBottom: '1px solid #e8eaed' }}>
        <img src={cwyLogo} alt="CWY Logo" style={{ width: 140, height: 'auto' }} />
        <Typography variant="caption" sx={{ display: 'block', color: '#606060', mt: 0.5 }}>
          CWY ADMIN PORTAL
        </Typography>
      </Box>
      <List sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <Typography variant="overline" sx={{ px: 2, color: '#909090' }}>
          Work
        </Typography>
        {[...nav.primaryWork, ...nav.moreWork].map((item) => {
          const active = isNavItemActive(location.pathname, item.path);
          return (
            <ListItemButton
              key={`work-${item.path}`}
              component={RouterLink}
              to={withLedgerBookQuery(item.path)}
              selected={active}
              onClick={(event) => handleAppLinkClick(event, item.path)}
            >
              <ListItemIcon sx={{ minWidth: 40, color: active ? '#0f0f0f' : '#606060' }}>
                <NavIcon iconKey={item.iconKey} />
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          );
        })}
        {nav.adminHubs.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="overline" sx={{ px: 2, color: '#909090' }}>
              Back Office
            </Typography>
            {nav.adminHubs.map((item) => {
              const active = isNavItemActive(location.pathname, item.path);
              return (
                <ListItemButton
                  key={`admin-${item.path}`}
                  component={RouterLink}
                  to={withLedgerBookQuery(item.path)}
                  selected={active}
                  onClick={(event) => handleAppLinkClick(event, item.path)}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: active ? '#0f0f0f' : '#606060' }}>
                    <NavIcon iconKey={item.iconKey} />
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              );
            })}
          </>
        )}
        <Divider sx={{ my: 1 }} />
        <Typography variant="overline" sx={{ px: 2, color: '#909090' }}>
          Me
        </Typography>
        {nav.meMenu.map((item) => {
          const active = isNavItemActive(location.pathname, item.path);
          return (
            <ListItemButton
              key={`me-${item.path}`}
              component={RouterLink}
              to={withLedgerBookQuery(item.path)}
              selected={active}
              onClick={(event) => handleAppLinkClick(event, item.path)}
            >
              <ListItemIcon sx={{ minWidth: 40, color: active ? '#0f0f0f' : '#606060' }}>
                <NavIcon iconKey={item.iconKey} />
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          );
        })}
        <ListItemButton onClick={handleLogout}>
          <ListItemIcon sx={{ minWidth: 40, color: '#606060' }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        className="app-topbar"
        sx={{
          zIndex: Z_INDEX.SIDEBAR,
          backgroundColor: '#ffffff',
          color: '#0f0f0f',
          borderBottom: '1px solid #e8eaed',
          '@media print': { display: 'none !important' },
        }}
      >
        <Toolbar
          sx={{
            minHeight: `${TOP_BAR_HEIGHT}px !important`,
            height: TOP_BAR_HEIGHT,
            px: { xs: 2.5, sm: 4, md: 5, lg: 6 },
            gap: 0.5,
          }}
        >
          {isMobile && (
            <IconButton
              edge="start"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
              sx={{ color: '#606060', mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box
            component={RouterLink}
            to="/app"
            onClick={(event) => handleAppLinkClick(event, '/app')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              mr: { xs: 1, md: 2 },
              flexShrink: 0,
              textDecoration: 'none',
              '&:hover': { opacity: 0.85 },
            }}
          >
            <img src={cwyLogo} alt="CWY" style={{ height: 36, width: 'auto' }} />
          </Box>

          {!isMobile && (
            <Box
              component="nav"
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 0,
                flex: 1,
                minWidth: 0,
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }}
            >
              {nav.primaryWork.map((item) => {
                const active = isNavItemActive(location.pathname, item.path);
                return renderNavTab({
                  key: item.path,
                  label: item.text,
                  active,
                  ariaCurrent: active,
                  to: withLedgerBookQuery(item.path),
                  onClick: (event) => handleAppLinkClick(event, item.path),
                  icon: (
                    <UnreadBadge count={item.unreadCount}>
                      <NavIcon iconKey={item.iconKey} />
                    </UnreadBadge>
                  ),
                });
              })}

              {nav.moreWork.length > 0 && (
                <>
                  {renderNavTab({
                    key: 'more',
                    label: 'More',
                    active: moreActive,
                    onClick: (e) => setMoreAnchor(e.currentTarget),
                    icon: <MoreHorizOutlinedIcon sx={ICON_SX} />,
                    showChevron: true,
                  })}
                  <Menu
                    anchorEl={moreAnchor}
                    open={Boolean(moreAnchor)}
                    onClose={() => setMoreAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {renderMenuItems(nav.moreWork)}
                  </Menu>
                </>
              )}

              {nav.adminHubs.length > 0 && (
                <>
                  {renderNavTab({
                    key: 'back-office',
                    label: 'Back Office',
                    active: adminActive,
                    onClick: (e) => setAdminAnchor(e.currentTarget),
                    icon: <BusinessCenterOutlinedIcon sx={ICON_SX} />,
                    showChevron: true,
                  })}
                  <Menu
                    anchorEl={adminAnchor}
                    open={Boolean(adminAnchor)}
                    onClose={() => setAdminAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    {renderMenuItems(nav.adminHubs)}
                  </Menu>
                </>
              )}
            </Box>
          )}

          {isMobile && <Box sx={{ flex: 1 }} />}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 'auto', flexShrink: 0 }}>
            {nav.showMailbox && (
              <Tooltip title="Mailbox">
                <IconButton
                  component={RouterLink}
                  to="/app/mailbox"
                  aria-label="Mailbox"
                  onClick={(event) => handleAppLinkClick(event, '/app/mailbox')}
                  sx={{
                    color: location.pathname.startsWith('/app/mailbox')
                      ? NAV_TEXT_PRIMARY
                      : NAV_TEXT_SECONDARY,
                  }}
                >
                  <UnreadBadge count={mailUnread || undefined}>
                    <MailOutlineIcon />
                  </UnreadBadge>
                </IconButton>
              </Tooltip>
            )}
            {nav.showNotifications && (
              <IconButton
                component={RouterLink}
                to="/app/notifications"
                aria-label="Notifications"
                onClick={(event) => handleAppLinkClick(event, '/app/notifications')}
                sx={{
                  color: notificationsActive ? NAV_TEXT_PRIMARY : NAV_TEXT_SECONDARY,
                }}
              >
                <UnreadBadge count={noticeUnread || undefined}>
                  {notificationsActive ? (
                    <NotificationsIcon />
                  ) : (
                    <NotificationsNoneOutlinedIcon />
                  )}
                </UnreadBadge>
              </IconButton>
            )}

            <Tooltip title="Open Agent Phone">
              <IconButton
                aria-label="Agent Phone"
                onClick={() => navigateToAgentPhoneScreen()}
                sx={{
                  color: onCall ? 'primary.main' : NAV_TEXT_SECONDARY,
                  bgcolor: onCall ? 'rgba(255, 0, 0, 0.08)' : 'transparent',
                  '&:hover': {
                    bgcolor: onCall ? 'rgba(255, 0, 0, 0.12)' : '#f1f3f4',
                  },
                }}
              >
                <Badge
                  variant="dot"
                  color="error"
                  invisible={!onCall}
                  overlap="circular"
                >
                  <CallOutlinedIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                mx: 0.5,
                my: 1.25,
                borderColor: '#dadce0',
              }}
            />

            <Box
              onClick={(e) => setUserAnchor(e.currentTarget)}
              role="button"
              aria-label="User menu"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                cursor: 'pointer',
                borderRadius: 1,
                px: 0.5,
                py: 0.25,
                '&:hover': { backgroundColor: '#f1f3f4' },
              }}
            >
              <Avatar
                key={user?.profileImageUrl || 'no-image'}
                src={user?.profileImageUrl || undefined}
                sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '0.875rem' }}
              >
                {!user?.profileImageUrl && user?.firstName?.charAt(0)}
              </Avatar>
              <KeyboardArrowDownIcon sx={{ color: '#606060', fontSize: 20 }} />
            </Box>
            <Menu
              anchorEl={userAnchor}
              open={Boolean(userAnchor)}
              onClose={() => setUserAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" sx={{ color: '#606060' }}>
                  {user?.email || user?.username}
                </Typography>
              </Box>
              <Divider />
              {renderMenuItems(nav.meMenu)}
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          display: { md: 'none' },
          '@media print': { display: 'none !important' },
        }}
      >
        {mobileDrawer}
      </Drawer>
    </>
  );
});
